// FE client for the app-instance placement API (app-platform V3) — B5 Mounted-Apps tab.
//
// Uses the EXISTING wall-respecting list (inherit-DOWN): instances mounted at the reader node OR its
// ancestors = "the apps mounted at / visible from this node". A subtree query is intentionally NOT
// used — it would let an ancestor (e.g. the workspace) read DESCENDANT placements, bypassing the
// ethical wall (scope_pdp). Per the brief guardrail, governance in the way is a finding, not an
// obstacle to engineer around. So the Mounted-Apps tab shows what is genuinely visible at the node.
import { classifyFetchError } from "../state/denialErrors";
import { invalidateScopedPages } from "../../lib/queryClient";

export interface AppInstance {
  id: string;
  app_id: string;
  scope_node_id: string;
  scope_path: string;
  zone: string;
  status: string;
  placed_by: string;
}

export const APP_INSTANCES_CHANGED_EVENT = "workspace:app-instances-changed";

function notifyAppInstancesChanged(scopeNodeId: string): void {
  // Inherited placements affect descendant readers too. Invalidate once at the write boundary;
  // every mounted consumer shares the resulting request through the query cache.
  invalidateScopedPages();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(APP_INSTANCES_CHANGED_EVENT, { detail: { scopeNodeId } }));
  }
}

export async function fetchAppInstances(
  readerScopeNodeId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AppInstance[]> {
  const res = await fetchImpl(
    `/api/workspace/app-instances?reader_scope_node_id=${encodeURIComponent(readerScopeNodeId)}`,
    { method: "GET", credentials: "include" },
  );
  if (!res.ok) throw await classifyFetchError(res, "fetchAppInstances");
  const data = (await res.json()) as AppInstance[];
  // Active placements only — a removed instance keeps its row (audit) but isn't a live mount.
  return data.filter((a) => a.status === "active");
}

/** Persist a mount before opening an app. Duplicate placement is idempotent for UI callers. */
export async function placeAppInstance(
  appId: string,
  scopeNodeId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AppInstance> {
  const res = await fetchImpl("/api/workspace/app-instances", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ app_id: appId, scope_node_id: scopeNodeId }),
  });
  if (res.status === 409) {
    const instances = await fetchAppInstances(scopeNodeId, fetchImpl);
    const existing = instances.find((item) => item.app_id === appId && item.scope_node_id === scopeNodeId);
    if (existing) {
      notifyAppInstancesChanged(scopeNodeId);
      return existing;
    }
  }
  if (!res.ok) throw await classifyFetchError(res, "placeAppInstance");
  const instance = (await res.json()) as AppInstance;
  notifyAppInstancesChanged(scopeNodeId);
  return instance;
}
